import { MemoizedSelector, createSelector } from '@ngrx/store';

import { DataSpacePermission } from '@core/models/dataspace/data-space-permission.enum';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { JobStatusUtils } from '@core/models/optimization.model';
import { PlanWatchHolder } from '@core/models/plan-edit.model';
import { selectHasUserPermission } from '@core/store/auth/auth.selectors';
import { selectDataspaceModel } from '@core/store/dataspace/dataspace.selectors';
import { selectIsFeatureActive } from '@core/store/feature-flags/feature-flags.selectors';
import { selectFindings } from '@core/store/plan-findings/plan-findings.selectors';
import { selectPlan } from '@core/store/plan/plan.selectors';
import { derivePins, deriveRouteStarts } from '@core/utils/plan-edit/pins';

import { selectPlanEditState, selectPlanWatch } from './plan-edit.selectors';
import {
  ImpactRound,
  PlanEditSessionState,
  PlanWatchState,
  watchIsLiveFor
} from './plan-edit.state';

export const selectPlanEditSession = createSelector(
  selectPlanEditState,
  (state) => state.session
);

/**
 * The watch with the routes of gestures still awaiting their answer written over the overlay, so a
 * view shows an edit the moment it is made. The watch itself when nothing is predicted, so signals
 * reading it see no change.
 */
export const selectPlanWatchForView = createSelector(
  selectPlanWatch,
  selectPlanEditSession,
  (watch, session): PlanWatchState => {
    if (session.inFlight.length === 0 || session.planId !== watch.planId) return watch;
    const routes = { ...watch.overlay.upserted['UserRoute'] };
    for (const gesture of session.inFlight) {
      Object.assign(routes, gesture.predicted);
    }
    return {
      ...watch,
      overlay: {
        ...watch.overlay,
        upserted: { ...watch.overlay.upserted, UserRoute: routes }
      }
    };
  }
);

export const selectPlanEditImpact = createSelector(selectPlanEditSession, (s) => s.impact);

export const selectPendingRouteUids = createSelector(
  selectPlanEditSession,
  (session) =>
    new Set([
      ...session.inFlight.flatMap((g) => g.touchedRouteUids),
      ...(session.awaitingUndo?.touchedRouteUids ?? []),
      ...session.recomputingRouteUids
    ])
);

/** The routes edited since the session opened or last saved; an undone gesture no longer counts. */
export const selectTouchedRouteUids = createSelector(
  selectPlanEditSession,
  (session) =>
    new Set(session.applied.slice(0, session.cursor).flatMap((g) => g.touchedRouteUids))
);

export const selectSessionPins = createSelector(selectPlanEditSession, (session) =>
  derivePins(session.pinsAtSave, session.applied, session.cursor)
);

/** The departures the session holds, by route uid. */
export const selectSessionRouteStarts = createSelector(selectPlanEditSession, (session) =>
  deriveRouteStarts(session.routeStartsAtSave, session.applied, session.cursor)
);

/**
 * The routes the session opened, which no stored page holds: those DL minted, then those still
 * opening, under the ref their batch names them by. A route removed since is not among them.
 */
export const selectOpenedRouteUids = createSelector(selectPlanEditSession, (session) => {
  const opening = session.inFlight.flatMap(({ gesture }) =>
    gesture.kind === 'open-route' ? [gesture.routeRef] : []
  );
  return [...session.openedRouteUids, ...opening].filter(
    (uid) => !session.removedRouteUids.includes(uid)
  );
});

/** The routes the session removed, those whose removal is still in flight included. */
export const selectHiddenRouteUids = createSelector(
  selectPlanEditSession,
  (session) =>
    new Set([
      ...session.removedRouteUids,
      ...session.inFlight.flatMap(({ gesture }) =>
        gesture.kind === 'remove-route' ? [gesture.routeUid] : []
      )
    ])
);

/**
 * The edits ending the session now would lose: those applied since the last save, and those DL has
 * yet to answer, which it would apply only for the close to throw away. An undo in flight still
 * counts the edit it takes back until DL answers it.
 */
export function unsavedEditCount(session: PlanEditSessionState): number {
  const redoing = session.awaitingUndo?.direction === 'redo' ? 1 : 0;
  return session.unsavedCount + session.inFlight.length + redoing;
}

/** Undo waits for every gesture to be answered, so it never steps an entry the planner did not mean. */
function stackIsFree(session: PlanEditSessionState): boolean {
  return (
    session.mode === 'editing' &&
    session.inFlight.length === 0 &&
    session.awaitingUndo === null
  );
}

export const selectCanUndo = createSelector(
  selectPlanEditSession,
  (session) => stackIsFree(session) && session.cursor > 0
);

export const selectCanRedo = createSelector(
  selectPlanEditSession,
  (session) => stackIsFree(session) && session.cursor < session.applied.length
);

const editingHereSelectors = new Map<string, (state: object) => boolean>();

/** Memoised per plan id so repeated calls share one selector. */
export const selectIsEditingHere = (planId: string) => {
  const cached = editingHereSelectors.get(planId);
  if (cached) return cached;
  const selector = createSelector(
    selectPlanEditSession,
    (session) => session.mode !== 'off' && session.planId === planId
  );
  editingHereSelectors.set(planId, selector);
  return selector;
};

/** Whether this tab's session, in any mode but off, is on the plan in view. */
export const selectIsEditingPlanInView = createSelector(
  selectPlan,
  selectPlanEditSession,
  (plan, session) => !!plan && session.mode !== 'off' && session.planId === plan.id
);

const selectWatchImpact = createSelector(selectPlanEditState, (state) => state.watchImpact);

/**
 * Its own selector so the round keeps its identity across watch patches: consumers re-diff the
 * findings whenever the round object changes.
 */
export const selectWatchLastRound = createSelector(
  createSelector(selectWatchImpact, (impact) => impact.lastRound),
  selectFindings,
  (round, storedFindings): ImpactRound | null =>
    round?.storedBefore
      ? { ...round, before: { ...round.before, findings: storedFindings } }
      : round
);

export interface PlanChangesView {
  lastRound: ImpactRound | null;
  /** No change owed an answer: deltas may be shown. */
  settled: boolean;
  /** Who made the changes: null for this tab's own session. */
  by: PlanWatchHolder | null;
}

/**
 * The last change to the plan in view, from this tab's own session or from the one it watches. Own
 * edits are measured from the session, which knows its gestures (refused, undone or in flight) as
 * the watch cannot. A watched change that started from no state carrying findings takes its
 * findings before from the stored plan's check. Null when the plan in view has no live session.
 */
export const selectPlanChanges: MemoizedSelector<object, PlanChangesView | null> =
  createSelector(
    selectPlan,
    selectIsEditingPlanInView,
    selectPlanEditSession,
    selectPlanWatch,
    selectWatchImpact,
    selectWatchLastRound,
    (
      plan,
      editing,
      session,
      watch,
      watchImpact,
      watchLastRound
    ): PlanChangesView | null => {
      if (editing) {
        return {
          lastRound: session.impact.lastRound,
          settled:
            session.timings === 'settled' &&
            session.inFlight.length === 0 &&
            session.awaitingUndo === null,
          by: null
        };
      }
      if (!plan || !watchIsLiveFor(watch, plan.id) || watchImpact.planId !== plan.id) {
        return null;
      }
      return {
        lastRound: watchLastRound,
        settled: !watch.recomputePending && !watch.recomputeFailed,
        by: watch.holder
      };
    }
  );

/**
 * Whether an edit session holds the plan in view: this tab's own, or one the watch reports. GBE
 * still takes writes to the stored plan meanwhile, and the session's save then fails on the
 * conflict, so nothing writes the stored plan until the session ends.
 */
export const selectIsPlanInViewHeld = createSelector(
  selectPlan,
  selectPlanWatch,
  selectIsEditingPlanInView,
  (plan, watch, editing) =>
    editing || (!!plan && watch.planId === plan.id && watch.holder !== null)
);

export type EditDisabledReason = 'optimization_running' | 'held';

export interface EditAvailability {
  visible: boolean;
  disabledReason: EditDisabledReason | null;
}

const EDIT_DATA_SPACE_PERMISSIONS: readonly string[] = [
  DataSpacePermission.WRITE,
  DataSpacePermission.CUSTOM
];

export const selectEditAvailability = createSelector(
  selectIsFeatureActive(KNOWN_FEATURE_FLAGS.ENABLE_LIVE_PLAN_EDIT),
  selectHasUserPermission('live_plan_edit'),
  selectDataspaceModel,
  selectPlan,
  selectPlanWatch,
  selectPlanEditSession,
  (flag, permitted, dataspace, plan, watch, session): EditAvailability => {
    const dataspacePermission = dataspace?.permission;
    const visible =
      flag &&
      permitted &&
      (dataspacePermission === undefined ||
        EDIT_DATA_SPACE_PERMISSIONS.includes(dataspacePermission));
    if (!visible) return { visible: false, disabledReason: null };

    // The editing tab watches its own session, so the holder it sees is itself.
    const heldByOther =
      watch.holder !== null && !(session.mode !== 'off' && session.planId === watch.planId);
    let disabledReason: EditDisabledReason | null = null;
    if (plan?.childrenInfo?.some((c) => JobStatusUtils.isUnfinishedStatus(c.jobStatus))) {
      disabledReason = 'optimization_running';
    } else if (heldByOther) disabledReason = 'held';
    return { visible: true, disabledReason };
  }
);
